import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { handleStepConflict } from '@/lib/stepConflict';
import { lessonApi } from '@/lib/api-client';
import { describeFailure } from '@/lib/lesson-error';
import {
  optimisticallyMarkSourcesProcessing,
  optimisticallyMarkStepProcessing,
} from '@/lib/optimisticRetry';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import type { StepView, SourcesView } from '@/types/pipeline';
import { deriveBuildStages, titleState } from './build-view/buildStages';
import { BuildStepper } from './build-view/BuildStepper';
import { BuildPanel } from './build-view/BuildPanel';
import { BuildTitle } from './build-view/BuildTitle';

/**
 * The center panel's "watch it build" view, shown while the lesson's
 * foundation is still being assembled - i.e. before `byte_selection`
 * (context) completes and the real content tabs appear. It is up from the
 * first poll, even while the overview (title + summary) is still running.
 *
 * One screen, never scrolled, top to bottom:
 *   - the lesson's title (`BuildTitle`): the typed topic, shimmering, until
 *     the overview writes the real one. The overview is not a step - it runs
 *     beside the first one and nothing waits for it.
 *   - three steps in strict order (`build-view/buildStages.ts`, drawn by
 *     `BuildStepper`): reading / gathering the material, planning the lesson,
 *     building the lesson. Only one is ever active.
 *   - the active step's heading and hint.
 *   - what fits the active step (`BuildPanel`), sized to itself: a tip card
 *     while the material is read, what was found in it while the lesson is
 *     planned, the draft mind map while coverage is checked.
 * The group is centred in the panel; nothing stretches to fill it.
 *
 * This view is also the single home for the two foundation failures (each
 * with its own retry):
 *   - all sources failed  → retry sources
 *   - context build failed → retry byte_selection
 * Chat stays disabled meanwhile (handled by the chat panel). No content tabs.
 */

interface LessonBuildProgressProps {
  lessonId?: string;
  /** Source-ingestion rollup from pipeline-status (drives the source step). */
  sources?: SourcesView;
  /** The overview StepView (whether the title is still being written). */
  overview?: StepView;
  /**
   * The title to show: the typed topic until the overview writes the real
   * one (`displayLessonTitle`). Omitted where the layout already shows the
   * title (the phone top bar).
   */
  lessonTitle?: string;
  /** The byte_selection StepView (drives the building step + failure mode). */
  byteSelection?: StepView;
  /** Whether the user supplied their own sources (vs synthetic generation). */
  hasUserSources?: boolean;
  /** Refetch pipeline-status after a retry is queued, for snappier feedback. */
  onRetry?: () => void;
}

export function LessonBuildProgress({
  lessonId,
  sources,
  overview,
  byteSelection,
  hasUserSources,
  lessonTitle,
  onRetry,
}: LessonBuildProgressProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const queryClient = useQueryClient();
  const byteState = byteSelection?.state;
  const sourcesFailed = sources?.overall === 'failed';

  // --- Foundation failure: every source failed, so there's nothing to ground
  // on. Recovery re-ingests / regenerates sources (not a step retry). ---
  if (sourcesFailed) {
    // Context-aware: "your sources" only makes sense when the user actually
    // added some. For synthetic/no-source lessons we failed to *gather*
    // material from the prompt, so we say that instead.
    const failure = hasUserSources
      ? {
          title: "Couldn't read your sources",
          body: "We couldn't process any of the sources you added. Check them and try again, or add a different one.",
        }
      : {
          title: "Couldn't gather material",
          body: "We couldn't pull together enough material for this topic. Please try again.",
        };
    const handleRetry = async () => {
      if (!lessonId) return;
      setIsRetrying(true);
      // Flip source ingestion to "processing" instantly; rollback on failure.
      const rollback = optimisticallyMarkSourcesProcessing(
        queryClient,
        lessonId
      );
      try {
        await lessonApi.retrySources(lessonId);
        notify.success('Retrying');
        onRetry?.();
      } catch (err: unknown) {
        rollback();
        notify.error(err, "Couldn't retry");
      } finally {
        setIsRetrying(false);
      }
    };
    // Scope-explicit label so it reads differently from the per-source row
    // "Retry": "Retry all sources" when the user has sources, "Try again"
    // for synthetic lessons (there are no user sources to "retry all").
    const retryLabel = hasUserSources ? 'Retry all sources' : 'Try again';
    return (
      <PanelFallback
        variant='panel'
        title={failure.title}
        description={failure.body}
        primary={
          lessonId
            ? { label: retryLabel, onClick: handleRetry, busy: isRetrying }
            : undefined
        }
        secondary={{
          label: 'Reload page',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  // --- Foundation failure: context build failed. ---
  if (byteState === 'failed') {
    // Keep code-specific copy (rate-limited, timed out, ...) but rephrase the
    // generic context failure so it doesn't assume the user gave us sources.
    const ctxCode = byteSelection?.error?.code;
    const failure =
      !ctxCode || ctxCode === 'CONTEXT_GENERATION_FAILED'
        ? {
            title: "Couldn't build your lesson",
            body: hasUserSources
              ? 'We had trouble making sense of your sources. Please try again.'
              : 'Something went wrong while building your lesson. Please try again.',
          }
        : describeFailure(byteSelection?.error, 'byte_selection');
    const handleRetry = async () => {
      if (!lessonId) return;
      setIsRetrying(true);
      // Flip byte_selection to "processing" instantly; rollback on failure.
      const rollback = optimisticallyMarkStepProcessing(
        queryClient,
        lessonId,
        'byte_selection'
      );
      try {
        await lessonApi.retryStep(lessonId, 'byte_selection');
        notify.success('Retry queued');
        onRetry?.();
      } catch (err: unknown) {
        rollback();
        // 409 = the context is already built: refresh instead of erroring.
        if (!handleStepConflict(err, queryClient, lessonId)) {
          notify.error(err, "Couldn't queue retry");
        }
      } finally {
        setIsRetrying(false);
      }
    };
    return (
      <PanelFallback
        variant='panel'
        title={failure.title}
        description={failure.body}
        primary={
          lessonId
            ? { label: 'Retry', onClick: handleRetry, busy: isRetrying }
            : undefined
        }
        secondary={{
          label: 'Reload page',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  // --- Progress: title, three steps, the active step, one stage card. ---
  const { stages, current, panel } = deriveBuildStages({
    sources,
    byteSelection,
    hasUserSources,
  });

  return (
    // Sized to the panel and never scrolled: the stage card takes what is
    // left and fits its content to it.
    <div
      role='region'
      aria-labelledby='lesson-build-heading'
      className='animate-fade-in-up flex h-full w-full flex-col items-center overflow-hidden px-6 py-6 motion-reduce:animate-none sm:px-8 sm:py-8'
    >
      <div className='flex h-full min-h-0 w-full max-w-2xl flex-col justify-center'>
        {lessonTitle !== undefined && (
          <BuildTitle title={lessonTitle} state={titleState(overview)} />
        )}

        <div className='mt-6 shrink-0'>
          <BuildStepper stages={stages} />
        </div>

        <div className='mt-7 shrink-0 text-center'>
          <h2
            id='lesson-build-heading'
            className='text-xl font-semibold text-foreground'
          >
            {current.heading}…
          </h2>
          <p className='mx-auto mt-1.5 max-w-md text-sm text-muted-foreground'>
            {current.hint}
          </p>
        </div>

        <div className='mt-6 flex min-h-0 shrink justify-center'>
          <BuildPanel
            lessonId={lessonId}
            panel={panel}
            sourcesDone={sources?.overall === 'completed'}
            byteSelection={byteSelection}
            hasUserSources={hasUserSources}
          />
        </div>

        {/* No `/70` - at 12px it measured under AA, and this line sets the
            user's expectation for how long to wait, which is worth reading. */}
        <p className='mt-5 shrink-0 text-center text-xs text-muted-foreground'>
          Usually 1–3 minutes in all. You can leave this open.
        </p>
      </div>
    </div>
  );
}
